/**
 * Every build attempt for a script, across all its versions, with its console.
 *
 * The per-version "Builds" panel under Version history only ever shows one commit's
 * attempts, so a failure against a commit nobody has open is easy to miss entirely.
 * This is the script-wide view: newest first, with a build's CodeBuild console
 * available inline — the same thing a CodeBuild failure notification points you at,
 * without leaving the app.
 */

import { Fragment, useState } from "react";

import type { BuildSummary } from "@/api/endpoints";
import { endpoints } from "@/api/endpoints";
import { Console } from "@/components/Console";
import { useProject } from "@/components/ProjectLayout";
import { Badge, Empty, Loading, Notice, RelativeTime, formatDuration } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";
import { useBuildConsole } from "@/hooks/useBuildConsole";

function StatusBadge({ build }: { build: BuildSummary }) {
  switch (build.status) {
    case "succeeded":
      return <Badge tone="success">Succeeded</Badge>;
    case "failed":
      return <Badge tone="danger">Failed</Badge>;
    case "blocked":
      return <Badge tone="danger">Blocked by scan</Badge>;
    case "building":
      return (
        <Badge tone="accent" live>
          Building
        </Badge>
      );
    default:
      return <Badge>Pending</Badge>;
  }
}

function isInFlight(status: BuildSummary["status"]): boolean {
  return status === "pending" || status === "building";
}

function BuildConsole({ scriptRef, build }: { scriptRef: string; build: BuildSummary }) {
  const { project } = useProject();
  const live = isInFlight(build.status);
  const consoleState = useBuildConsole(project.slug, scriptRef, build.id, live);

  return (
    <div className="stack" style={{ gap: 10, padding: "0 18px 16px" }}>
      {build.error_message && (
        <Notice tone={build.status === "blocked" ? "warning" : "danger"}>
          {build.error_message}
        </Notice>
      )}
      <Console
        lines={consoleState.lines}
        streaming={consoleState.streaming}
        complete={consoleState.complete}
        error={consoleState.error}
      />
      {build.log_group && build.log_stream && (
        <div className="faint mono" style={{ fontSize: 11.5 }}>
          {build.log_group} / {build.log_stream}
        </div>
      )}
    </div>
  );
}

export function BuildHistory({ scriptRef }: { scriptRef: string }) {
  const { project } = useProject();
  const [expanded, setExpanded] = useState<string>();

  const { data, error, loading, reload } = useAsync(
    () => endpoints.scriptBuilds(project.slug, scriptRef),
    [project.slug, scriptRef],
    // Anything still building advances on its own; keep the list fresh while it does.
    { pollMs: 10_000 },
  );

  if (loading && !data) return <Loading rows={3} label="Loading builds" />;
  if (error) {
    return (
      <div className="card">
        <Notice tone="danger">{error.message}</Notice>
      </div>
    );
  }

  const builds = data ?? [];

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Builds</span>
        <span className="muted" style={{ fontSize: 12.5 }}>
          Every image build for this script, newest first
        </span>
      </div>

      {builds.length === 0 ? (
        <Empty title="No builds yet">
          A build appears here as soon as a version is built.
        </Empty>
      ) : (
        <div className="table__scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Commit</th>
                <th>Status</th>
                <th>Duration</th>
                <th>When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {builds.map((build) => {
                const isOpen = expanded === build.id;
                return (
                  <Fragment key={build.id}>
                    <tr>
                      <td className="mono">{build.short_sha}</td>
                      <td>
                        <StatusBadge build={build} />
                      </td>
                      <td className="muted mono">
                        {build.started_at && build.finished_at
                          ? formatDuration(
                              new Date(build.finished_at).getTime() -
                                new Date(build.started_at).getTime(),
                            )
                          : "—"}
                      </td>
                      <td className="muted">
                        <RelativeTime iso={build.created_at} />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          type="button"
                          className="button button--small button--ghost"
                          onClick={() => setExpanded(isOpen ? undefined : build.id)}
                        >
                          {isOpen ? "Hide logs" : "View logs"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={5} style={{ background: "var(--surface-sunken)", padding: 0 }}>
                          <BuildConsole scriptRef={scriptRef} build={build} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {loading && data && (
        <div style={{ padding: "0 18px 14px" }}>
          <button type="button" className="button button--small button--ghost" onClick={reload}>
            Refresh
          </button>
        </div>
      )}
    </div>
  );
}
